import React, { useEffect, useRef, useState } from "react";
import { decryptAES } from "../../Common/Axios/encrypt";
import getTranslation from "../../TranslationUtility/TranslationUtility";
import { FormatCardTypes, FormatDate, GetMerchantBgColor, ThemeData } from "../../Utility/Utility";
import arrowForward from "../../Assets/img/arrow_forward.svg";

// Inline (not an <img>) so its fill can follow the button's `color` via currentColor —
// the copy icon uses the same dynamic colour as the coupon-code header text/border.
const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="copyIconMask" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
      <rect width="16" height="16" fill="#D9D9D9" />
    </mask>
    <g mask="url(#copyIconMask)">
      <path
        d="M6 12.0002C5.63333 12.0002 5.31944 11.8696 5.05833 11.6085C4.79722 11.3474 4.66667 11.0335 4.66667 10.6668V2.66683C4.66667 2.30016 4.79722 1.98627 5.05833 1.72516C5.31944 1.46405 5.63333 1.3335 6 1.3335H12C12.3667 1.3335 12.6806 1.46405 12.9417 1.72516C13.2028 1.98627 13.3333 2.30016 13.3333 2.66683V10.6668C13.3333 11.0335 13.2028 11.3474 12.9417 11.6085C12.6806 11.8696 12.3667 12.0002 12 12.0002H6ZM6 10.6668H12V2.66683H6V10.6668ZM3.33333 14.6668C2.96667 14.6668 2.65278 14.5363 2.39167 14.2752C2.13056 14.0141 2 13.7002 2 13.3335V4.00016H3.33333V13.3335H10.6667V14.6668H3.33333Z"
        fill="currentColor"
      />
    </g>
  </svg>
);

// Exported so MerchantPage.js's scroll-lock can delay restoring the page's scrollbar until
// this same close animation has actually finished (see its own usage for why).
export const SHEET_TRANSITION_MS = 320;

const Accordion = ({ label, expanded, onToggle, html }) => {
  const bodyRef = useRef(null);

  return (
    <div className="offer-sheet-accordion">
      <button
        className="offer-sheet-link"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        {label}
        <span className={`offer-sheet-link-arr ${expanded ? "open" : ""}`}>
          <img src={arrowForward} alt="Arrow Forward" />
        </span>
      </button>
      <div
        ref={bodyRef}
        className={`offer-sheet-accordion-body ${expanded ? "expanded" : ""}`}
        style={{ maxHeight: expanded ? `${bodyRef.current?.scrollHeight || 1000}px` : "0px" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

const OfferDetailSheet = ({
  coupon,
  deal,
  dealCoupounFrequenciesList,
  login,
  clientCode,
  onClose,
  onVisitNow,
}) => {
  const translation = getTranslation();
  const themeData = ThemeData();
  const merchantBgColor = GetMerchantBgColor(deal, themeData);
  const [copied, setCopied] = useState(false);
  // Each accordion (terms/about) opens/closes independently — opening one
  // doesn't collapse the other.
  const [expandedSections, setExpandedSections] = useState([]);

  const [renderedCoupon, setRenderedCoupon] = useState(coupon);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (coupon) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setRenderedCoupon(coupon);
      return;
    }

    if (renderedCoupon) {
      setIsOpen(false);
      setExpandedSections([]);
      closeTimeoutRef.current = setTimeout(() => {
        setRenderedCoupon(null);
      }, SHEET_TRANSITION_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon]);

  // Runs after `renderedCoupon` actually lands in the DOM (a separate effect
  // from the one above, so the node below exists by the time this fires).
  // Reading offsetHeight forces the browser to synchronously flush layout
  // for the current (closed) styles before the class flips to .is-open —
  // more reliable across browsers/devices than guessing how many
  // requestAnimationFrame callbacks a real paint needs.
  useEffect(() => {
    if (!renderedCoupon) return;
    const node = sheetRef.current;
    if (node) {
      // eslint-disable-next-line no-unused-expressions
      node.offsetHeight;
    }
    const raf = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [renderedCoupon]);

  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((current) =>
      current.includes(section) ? current.filter((s) => s !== section) : [...current, section]
    );
  };

  const getCouponCodeFromCoupon = (couponData) => couponData?.coupounCode;

  const handleCopy = (couponData) => {
    let couponCode = "";
    if (couponData?.typeOfCoupoun === translation?.static) {
      couponCode = couponData?.coupounCode;
    } else if (couponData?.typeOfCoupoun === translation?.dynamic) {
      const dynamicCouponCode = dealCoupounFrequenciesList?.find(
        (freq) => freq.dealCoupounId === couponData.id && freq?.status === "Active"
      );
      couponCode = decryptAES(dynamicCouponCode?.coupounCode);
    }
    if (couponCode) {
      navigator.clipboard.writeText(couponCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (!renderedCoupon) return null;

  // While closing, `coupon` is already null — keep rendering the last known
  // coupon so the content doesn't blank out mid-transition.
  const activeCoupon = coupon || renderedCoupon;
  const expiryDate = FormatDate(
    dealCoupounFrequenciesList?.find((freq) => freq?.dealCoupounId === activeCoupon?.id)?.validUpto
  );
  const cardTypeLabel = FormatCardTypes(activeCoupon, translation);

  const badgeColors = {
    [translation?.badgeFeatured]: { bg: "var(--offer-badge-featured-bg, #8B4EC7)", text: "var(--offer-badge-featured-text, #FFFFFF)" },
    [translation?.badgeExpiringSoon]: { bg: "var(--offer-badge-expiring-bg, #9C4424)", text: "var(--offer-badge-expiring-text, #FFFFFF)" },
    [translation?.badgeNew]: { bg: "var(--offer-badge-new-bg, #0F8068)", text: "var(--offer-badge-new-text, #FFFFFF)" },
  };

  const canShowCta =
    activeCoupon?.cta &&
    (activeCoupon?.isStaccOffer !== true || (activeCoupon?.isStaccOffer === true && login));

  return (
    <div className={`offer-sheet-overlay ${isOpen ? "is-open" : ""}`} onClick={onClose}>
      <div ref={sheetRef} className={`offer-sheet ${isOpen ? "is-open" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="offer-sheet-header">
          <div className="offer-sheet-merchant">
            <img
              src={`${process.env.REACT_APP_CLIENT_IMAGE_URL}${deal?.commonImage}`}
              alt={deal?.name}
              className="offer-sheet-merchant-logo"
              style={merchantBgColor ? { backgroundColor: merchantBgColor } : undefined}
              onError={(e) => (e.target.src = `${process.env.REACT_APP_CLIENT_IMAGE_URL}${clientCode}/img/Placeholder.svg`)}
            />
            <div className="offer-sheet-merchant-name pl-3">{deal?.name}</div>
          </div>
          <button className="offer-sheet-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="offer-sheet-body">
          <div className="offer-sheet-headline">
            <h3 className="offer-sheet-title">{activeCoupon?.title}</h3>
            <div className="offer-sheet-tags">
              {activeCoupon?.offerBadge && (
                <span
                  className="offer-sheet-tag offer-sheet-tag-badge"
                  style={{
                    backgroundColor: badgeColors[activeCoupon?.offerBadge]?.bg,
                    color: badgeColors[activeCoupon?.offerBadge]?.text,
                  }}
                >
                  {activeCoupon?.offerBadge}
                </span>
              )}
              {/* {activeCoupon?.offerSubType && (
                <span className="offer-sheet-tag offer-sheet-tag-type">{activeCoupon?.offerSubType}</span>
              )} */}
              {cardTypeLabel && (
                <span className="offer-sheet-tag offer-sheet-tag-card">{cardTypeLabel}</span>
              )}
            </div>
          </div>

          <div className="offer-sheet-meta">
            {/* {(
              <div className="offer-sheet-meta-cell">
                <span className="offer-sheet-meta-label">{translation?.payWith}</span>
                <span className="offer-sheet-meta-value">{cardTypeLabel}</span>
              </div>
            )} */}
            <div className="offer-sheet-meta-cell">
              <span className="offer-sheet-meta-label">{translation?.expiresOn}</span>
              <span className="offer-sheet-meta-value">{expiryDate}</span>
            </div>
          </div>

          {activeCoupon?.typeOfCoupoun !== "NoCode" && (
            <div className="offer-sheet-code">
              {copied && <span className="offer-sheet-copied-toast">{translation?.copiedSuccessfully}</span>}
              <div>
                <span className="offer-sheet-code-label">{translation?.couponCodeLabel}</span>
                <span className="offer-sheet-code-value">{getCouponCodeFromCoupon(activeCoupon)}</span>
              </div>
              <button className="offer-sheet-copy" onClick={() => handleCopy(activeCoupon)}>
                Copy
                <CopyIcon />
              </button>
            </div>
          )}

          {canShowCta && (
            <button className="offer-sheet-cta" onClick={() => onVisitNow(activeCoupon)}>
              {translation?.visitNow}
            </button>
          )}

          {activeCoupon?.description && (
            <div className="offer-sheet-section">
              <h4>{translation?.offerDetails}</h4>
              <div dangerouslySetInnerHTML={{ __html: activeCoupon?.description }} />
            </div>
          )}

          {activeCoupon?.howToRedeem && (
            <div className="offer-sheet-section">
              <h4>{translation?.offerHowToRedeemTitle}</h4>
              <div dangerouslySetInnerHTML={{ __html: activeCoupon?.howToRedeem }} />
            </div>
          )}

          <div className="offer-sheet-links">
            {activeCoupon?.terms && (
              <Accordion
                label={translation?.termsAndConditions}
                expanded={expandedSections.includes("terms")}
                onToggle={() => toggleSection("terms")}
                html={activeCoupon?.terms}
              />
            )}
            {deal?.aboutBrand && (
              <Accordion
                label={`${translation?.aboutBrandLabel} ${deal?.name}`}
                expanded={expandedSections.includes("about")}
                onToggle={() => toggleSection("about")}
                html={deal?.aboutBrand}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferDetailSheet;
